/* eslint-disable jira/filename-case, jira/naming/disallow-filenames */
import flatMap from 'lodash/flatMap';
import map from 'lodash/map';
import { AriGraphSortOrderInput } from '@jira/devops__ari-graph-filters/src/types.tsx';
import { VulnerabilitySeverityEnums, VulnerabilityStatusEnums } from './QueryParams.types.tsx';

const DELIMITER = ',';
const ENCODED_DELIMITER = encodeURIComponent(DELIMITER);

// We encode the delimiter to protect the values from containing
// the same character that delimits the list.
// e.g.
// env names = ["prod,east", "prod,west"] -> "prod,east,prod,west"
const encodeDelimiter = (input: string) => {
	const regex = new RegExp(DELIMITER, 'g');
	return input.replace(regex, ENCODED_DELIMITER);
};

const decodeDelimiter = (input: string) => {
	const regex = new RegExp(ENCODED_DELIMITER, 'g');
	return input.replace(regex, DELIMITER);
};

export const arrayQueryParamToFilter = (queryParamString: string | null | undefined): string[] =>
	queryParamString?.split(DELIMITER)?.map(decodeDelimiter) ?? [];

export const arrayFilterToQueryParam = (filterArray: string[]): string | undefined => {
	if (filterArray.length === 0) return undefined;

	return filterArray.map((entity) => encodeDelimiter(entity)).join(DELIMITER);
};

export const connectionFilterToQueryParam = (connectionArray: string[]): string | undefined => {
	if (connectionArray.length === 0) return undefined;
	const groupedConnectionsByAppKey = connectionArray.reduce<Record<string, string[]>>(
		(acc, connection) => {
			const [, , , , securityParts] = connection.split(':');
			const [, , , appKey, ...containerId] = securityParts.split('/');
			if (!acc[appKey]) {
				acc[appKey] = [];
			}
			acc[appKey].push(containerId.join('/'));
			return acc;
		},
		{},
	);
	return JSON.stringify(groupedConnectionsByAppKey);
};

export const connectionQueryParamToFilter = (
	queryParamString: string | null | undefined,
	connectionAri: string,
): string[] => {
	if (!queryParamString) return [];
	const groupedConnectionsByAppKey = JSON.parse(queryParamString);
	return flatMap(groupedConnectionsByAppKey, (containerIds, appKey) =>
		map(containerIds, (containerId) => `${connectionAri}${appKey}/${containerId}`),
	);
};

export const parseSeverity = (
	maybeSeverity: string | null | undefined,
): VulnerabilitySeverityEnums | undefined => {
	switch (maybeSeverity?.toUpperCase()) {
		case VulnerabilitySeverityEnums.CRITICAL.toUpperCase():
			return VulnerabilitySeverityEnums.CRITICAL;
		case VulnerabilitySeverityEnums.HIGH.toUpperCase():
			return VulnerabilitySeverityEnums.HIGH;
		case VulnerabilitySeverityEnums.LOW.toUpperCase():
			return VulnerabilitySeverityEnums.LOW;
		case VulnerabilitySeverityEnums.MEDIUM.toUpperCase():
			return VulnerabilitySeverityEnums.MEDIUM;
		case VulnerabilitySeverityEnums.UNKNOWN.toUpperCase():
			return VulnerabilitySeverityEnums.UNKNOWN;
		default:
			return undefined;
	}
};
export const parseStatus = (
	maybeStatus: string | null | undefined,
): VulnerabilityStatusEnums | undefined => {
	switch (maybeStatus?.toUpperCase()) {
		case VulnerabilityStatusEnums.OPEN.toUpperCase():
			return VulnerabilityStatusEnums.OPEN;
		case VulnerabilityStatusEnums.UNKNOWN.toUpperCase():
			return VulnerabilityStatusEnums.UNKNOWN;
		case VulnerabilityStatusEnums.CLOSED.toUpperCase():
			return VulnerabilityStatusEnums.CLOSED;
		case VulnerabilityStatusEnums.IGNORED.toUpperCase():
			return VulnerabilityStatusEnums.IGNORED;
		default:
			return undefined;
	}
};
export const parseSortDirection = (
	maybeDirection: string | undefined | null,
	defaultValue: AriGraphSortOrderInput,
): AriGraphSortOrderInput => {
	switch (maybeDirection?.toUpperCase()) {
		case AriGraphSortOrderInput.ASC.toUpperCase():
			return AriGraphSortOrderInput.ASC;
		case AriGraphSortOrderInput.DESC.toUpperCase():
			return AriGraphSortOrderInput.DESC;
		default:
			return defaultValue;
	}
};
